import { Link, useLocation } from 'react-router-dom';
import {
  Moon,
  Sun,
  Wifi,
  WifiOff,
  Loader2,
  HelpCircle,
  Languages,
  ChevronRight,
  Menu,
  LogOut,
} from 'lucide-react';
import { useApp } from '@/store';
import { logout, getDecoded } from '@/lib/auth';
import { Theme, useTheme } from '@/lib/theme';
import { useT, LANGUAGES, type I18nKey } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const ROLE_LABEL: Record<string, I18nKey> = {
  org_admin: 'role.orgAdmin',
  super_admin: 'role.superAdmin',
  staff: 'role.staff',
  technician: 'role.technician',
};

export function Header() {
  const loc = useLocation();
  const online = useApp(s => s.online);
  const firstSyncComplete = useApp(s => s.firstSyncComplete);
  const syncTarget = useApp(s => s.syncTarget);
  const syncLoaded = useApp(s => s.syncLoaded);
  // Three-state sync pill: offline > syncing > online-synced.
  const syncing = online && !firstSyncComplete && syncTarget != null;
  const startTour = useApp(s => s.startTour);
  // Tour covers the full admin feature set → only org/super admins get the
  // trigger. (Auto-start was removed; the tour is manual-only.)
  const tourRoles = getDecoded()?.roles ?? [];
  const canTour =
    tourRoles.includes('org_admin') || tourRoles.includes('super_admin');
  const lang = useApp(s => s.lang);
  const setLang = useApp(s => s.setLang);
  const toggleSidebar = useApp(s => s.toggleSidebar);
  const companyName = useApp(s => s.settings.companyName);
  const currentUser = useApp(s => s.currentUser);
  // Show the logged-in user's own name: match their worker record by email,
  // then fall back to the JWT name / email. Previously this picked the FIRST
  // admin worker in the tenant, so every user saw that admin's name (e.g. the
  // top-right always showed "Felix" regardless of who was signed in).
  const myWorker = useApp(s =>
    s.workers.find(w => w.active && w.email === currentUser?.email)
  );
  const displayName =
    myWorker?.name ?? currentUser?.name ?? currentUser?.email ?? '';
  const initials = displayName
    ? displayName
        .split(/[\s@.]/)
        .filter(Boolean)
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';
  const { theme, toggle } = useTheme();
  const t = useT();
  const fmtRole = (r: string): string =>
    ROLE_LABEL[r] ? t(ROLE_LABEL[r]) : r.replace(/_/g, ' ');

  const crumbs = loc.pathname.replace(/^\//, '').split('/').filter(Boolean);
  const effective = crumbs.length ? crumbs : ['overview'];
  const toPascal = (s: string) =>
    s
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map(w => w[0].toUpperCase() + w.slice(1))
      .join(' ');

  return (
    <header className="no-print sticky top-0 z-20 flex h-14 items-center justify-between border-b bg-background/80 backdrop-blur-md px-3 sm:px-6">
      <div className="flex items-center gap-2 text-sm min-w-0">
        {/* Hamburger — visible below lg */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 shrink-0 lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-4 w-4" />
        </Button>

        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <img
            src="/favicon.svg"
            alt=""
            aria-hidden="true"
            className="h-3.5 w-3.5 rounded-sm"
          />
          {companyName}
        </span>
        {effective.map((c, i) => {
          const href = '/' + effective.slice(0, i + 1).join('/');
          const isLast = i === effective.length - 1;
          return (
            <span key={i} className="hidden sm:flex items-center gap-2">
              <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
              {isLast ? (
                <span className="text-xs font-medium text-foreground">
                  {toPascal(c)}
                </span>
              ) : (
                <Link
                  to={href}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  {toPascal(c)}
                </Link>
              )}
            </span>
          );
        })}
        {/* Mobile: show only last crumb */}
        <span className="sm:hidden text-sm font-medium text-foreground truncate">
          {toPascal(effective[effective.length - 1])}
        </span>
      </div>
      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
        <div
          className={cn(
            'flex items-center gap-1 sm:gap-1.5 rounded-full px-2 sm:px-3 h-8',
            !online
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
              : syncing
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
                : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
          )}
          data-tour="online-btn"
          aria-live="polite"
        >
          {!online ? (
            <WifiOff className="h-3.5 w-3.5" />
          ) : syncing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wifi className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline text-xs font-medium">
            {!online
              ? t('header.offline')
              : syncing
                ? syncTarget
                  ? `${syncLoaded}/${syncTarget}`
                  : t('header.syncing')
                : t('header.online')}
          </span>
        </div>

        {canTour && (
          <Button
            variant="ghost"
            size="sm"
            onClick={startTour}
            className="gap-1.5 h-8 rounded-full px-2 sm:px-3 hover:bg-orange-50 hover:text-orange-700 dark:hover:bg-orange-950/40 dark:hover:text-orange-400"
            aria-label={t('header.tour')}
            data-tour="help-btn"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            <span className="hidden sm:inline text-xs font-medium">
              {t('header.tour')}
            </span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-8 rounded-full px-2 sm:px-3"
              aria-label={t('header.lang')}
              data-tour="lang-btn"
            >
              <Languages className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                {lang}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-40 max-h-64 overflow-y-auto"
          >
            {LANGUAGES.map(l => (
              <DropdownMenuItem
                key={l.code}
                onClick={() => setLang(l.code)}
                className="justify-between"
              >
                {l.label}{' '}
                {lang === l.code && <span className="text-primary">✓</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          aria-label="Toggle theme"
          className="h-8 w-8 rounded-full"
        >
          {theme === Theme.DARK ? (
            <Sun className="h-3.5 w-3.5" />
          ) : (
            <Moon className="h-3.5 w-3.5" />
          )}
        </Button>

        {currentUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('auth.signOut')}
                className="ml-1 sm:ml-2 flex items-center gap-2 rounded-full border bg-card/50 pr-2 sm:pr-3 pl-1 py-1 hover:bg-muted transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full flame-gradient text-[10px] font-bold text-white">
                  {initials}
                </div>
                <span className="hidden sm:inline text-xs font-medium max-w-[140px] truncate">
                  {displayName}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                <div className="font-medium text-foreground truncate">
                  {currentUser.email}
                </div>
                {currentUser.roles?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {currentUser.roles
                      .filter(r => !r.startsWith('tenant:'))
                      .map(r => (
                        <Badge key={r} variant="secondary">
                          {fmtRole(r)}
                        </Badge>
                      ))}
                  </div>
                )}
              </div>
              <DropdownMenuItem
                onClick={() => {
                  // Hard reload after logout discards in-memory singletons
                  // (PouchDB handles, live sync feeds) so the next sign-in
                  // starts from a clean replica with no cross-tenant bleed.
                  void logout().finally(() => window.location.reload());
                }}
                className="text-destructive focus:text-destructive"
                data-testid="user-menu-signout"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('auth.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
